import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

const NET_RATE  = 0.9604;
const FEE_RATE  = 0.0396;

function formatYen(n) {
  return `¥${Number(n || 0).toLocaleString('ja-JP')}`;
}

function StatusBadge({ status, t }) {
  const map = {
    completed: 'badge-completed',
    pending:   'badge-pending',
    failed:    'badge-failed',
    cancelled: 'badge-cancelled',
  };
  return (
    <span className={`badge rounded-pill px-3 py-2 ${map[status] || 'badge-pending'}`}>
      {t(`admin.status_${status}`) || status}
    </span>
  );
}

function Pagination({ page, pages, total, limit, onPage, t }) {
  if (pages <= 1) return null;
  const start = (page - 1) * limit + 1;
  const end   = Math.min(page * limit, total);

  const pageNums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i++) {
    pageNums.push(i);
  }

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center px-4 py-3 border-top bg-light gap-2">
      <span className="text-muted small">
        {t('admin.pageShowing', { start, end, total })}
      </span>
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          disabled={page <= 1} onClick={() => onPage(1)}>
          <i className="bi bi-chevron-double-left" />
        </button>
        <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <i className="bi bi-chevron-left" />
        </button>
        {pageNums.map(n => (
          <button key={n}
            className={`btn btn-sm rounded-pill px-3 ${n === page ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => onPage(n)}>
            {n}
          </button>
        ))}
        <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          disabled={page >= pages} onClick={() => onPage(page + 1)}>
          <i className="bi bi-chevron-right" />
        </button>
        <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          disabled={page >= pages} onClick={() => onPage(pages)}>
          <i className="bi bi-chevron-double-right" />
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const navigate    = useNavigate();
  const token       = localStorage.getItem('adminToken');

  const [activeTab, setActiveTab] = useState('transactions');

  const [donations, setDonations] = useState([]);
  const [stats,     setStats]     = useState(null);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [pages,     setPages]     = useState(1);
  const [search,    setSearch]    = useState('');
  const [status,    setStatus]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [apiError,  setApiError]  = useState('');

  const [donors,       setDonors]       = useState([]);
  const [donorTotal,   setDonorTotal]   = useState(0);
  const [donorPage,    setDonorPage]    = useState(1);
  const [donorPages,   setDonorPages]   = useState(1);
  const [donorSearch,  setDonorSearch]  = useState('');
  const [donorLoading, setDonorLoading] = useState(false);
  const [donorError,   setDonorError]   = useState('');


  const api = useCallback(() =>
    axios.create({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    api().get('/api/admin/stats')
      .then(r => setStats(r.data))
      .catch(err => console.warn('Stats error:', err?.response?.data || err.message));
  }, [token, navigate, api]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setApiError('');
    const params = new URLSearchParams({ page, limit: 20 });
    if (status) params.append('status', status);
    if (search) params.append('search', search);

    api().get(`/api/admin/donations?${params}`)
      .then(r => {
        setDonations(r.data.donations);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setLoading(false);
      })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.clear(); navigate('/admin'); }
        setApiError(err.response?.data?.error || err.message || 'Unknown error');
        setLoading(false);
      });
  }, [token, page, search, status, navigate, api]);

  useEffect(() => {
    if (!token || activeTab !== 'donors') return;
    setDonorLoading(true);
    setDonorError('');
    const params = new URLSearchParams({ page: donorPage, limit: 20 });
    if (donorSearch) params.append('search', donorSearch);

    api().get(`/api/admin/donors?${params}`)
      .then(r => {
        setDonors(r.data.donors);
        setDonorTotal(r.data.total);
        setDonorPages(r.data.pages);
        setDonorLoading(false);
      })
      .catch(err => {
        setDonorError(err.response?.data?.error || err.message || 'Unknown error');
        setDonorLoading(false);
      });
  }, [token, activeTab, donorPage, donorSearch, api]);

  function logout() { localStorage.clear(); navigate('/admin'); }

  async function openReceipt(id) {
    const locale = i18n.language || 'ja';
    try {
      const res = await fetch(`/api/admin/donations/${id}/receipt?locale=${locale}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      alert('Failed to open receipt: ' + err.message);
    }
  }

  const totalAmount = stats?.totals?.total_amount || 0;
  const netAmount   = Math.floor(totalAmount * NET_RATE);
  const feeAmount   = totalAmount - netAmount;

  const statCards = stats ? [
    { icon: 'bi-collection-fill',  label: t('admin.totalDonations'), value: stats.totals.total_donations, color: '#4361ee' },
    { icon: 'bi-currency-yen',     label: t('admin.totalAmount'),    value: formatYen(totalAmount),       color: '#d63384' },
    { icon: 'bi-wallet2',          label: t('admin.netAmount'),      value: formatYen(netAmount),         color: '#198754' },
    { icon: 'bi-percent',          label: t('admin.feeAmount'),      value: formatYen(feeAmount),         color: '#fd7e14' },
    { icon: 'bi-people-fill',      label: t('admin.uniqueDonors'),   value: stats.totals.unique_donors,   color: '#6f42c1' },
    { icon: 'bi-check-circle-fill',label: t('admin.completedCount'), value: stats.totals.completed,       color: '#0dcaf0' },
  ] : [];

  return (
    <>
      <Navbar />
      <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
        <div className="container py-4">

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1">{t('admin.dashTitle')}</h3>
              <p className="text-muted mb-0 small">Glocal Festival</p>
            </div>
            <button className="btn btn-outline-danger rounded-pill px-4" onClick={logout}>
              <i className="bi bi-box-arrow-right me-2" />{t('admin.logout')}
            </button>
          </div>

          {stats && (
            <div className="row g-3 mb-4">
              {statCards.map((s, i) => (
                <div key={i} className="col-6 col-md-4 col-lg-2">
                  <div className="card border-0 shadow-sm rounded-4 p-3 h-100">
                    <div className="d-flex flex-column gap-2">
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: '1.1rem' }} />
                      </div>
                      <div>
                        <div className="fw-bold" style={{ color: s.color, fontSize: '1.1rem' }}>{s.value}</div>
                        <div className="text-muted" style={{ fontSize: '.72rem' }}>{s.label}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {stats && (
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4"
              style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', color: '#fff' }}>
              <h6 className="fw-bold mb-3" style={{ color: 'rgba(255,255,255,.7)', fontSize: '.8rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                <i className="bi bi-graph-up-arrow me-2" />{t('admin.financialSummary')}
              </h6>
              {/* Amount to pay students — hero number */}
              <div className="text-center mb-4" style={{ borderBottom: '1px solid rgba(255,255,255,.1)', paddingBottom: 20 }}>
                <div style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.5)', marginBottom: 8, letterSpacing: '.05em' }}>
                  {t('admin.amountToDistribute')}
                </div>
                <div style={{ fontSize: '3rem', fontWeight: 900, color: '#4ade80', lineHeight: 1 }}>
                  {formatYen(netAmount)}
                </div>
                <div style={{ fontSize: '.85rem', color: '#4ade80', opacity: .8, marginTop: 6 }}>
                  {t('admin.youReceive')} (96.04%) — after Stripe fee
                </div>
              </div>
              {/* Breakdown row */}
              <div className="row g-3 text-center">
                <div className="col-6">
                  <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.5)', marginBottom: 4 }}>{t('admin.totalReceived')}</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fff' }}>{formatYen(totalAmount)}</div>
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.4)' }}>100% donated</div>
                </div>
                <div className="col-6">
                  <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.5)', marginBottom: 4 }}>{t('admin.feesCharged')} (3.96%)</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fb923c' }}>{formatYen(feeAmount)}</div>
                  <div style={{ fontSize: '.72rem', color: '#fb923c', opacity: .7 }}>Stripe fee</div>
                </div>
              </div>
            </div>
          )}

          {stats?.byFrequency?.length > 0 && (
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-bar-chart-fill me-2" style={{ color: '#4361ee' }} />
                {t('admin.freqBreakdown')}
              </h6>
              <div className="row g-2">
                {stats.byFrequency.map(f => (
                  <div key={f.frequency} className="col-6 col-md-3">
                    <div className="p-3 rounded-3 text-center" style={{ background: '#f0f4ff' }}>
                      <div className="fw-bold" style={{ color: '#4361ee' }}>{f.count}</div>
                      <div className="small text-muted">{t(`admin.freq_${f.frequency}`)}</div>
                      <div className="small fw-semibold">{formatYen(f.sum)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ul className="nav nav-pills mb-3 gap-2">
            <li className="nav-item">
              <button
                className={`nav-link rounded-pill px-4 ${activeTab === 'transactions' ? 'active' : ''}`}
                style={activeTab === 'transactions' ? { background: '#1a1a2e' } : {}}
                onClick={() => setActiveTab('transactions')}>
                <i className="bi bi-receipt me-2" />{t('admin.tabTransactions')}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link rounded-pill px-4 ${activeTab === 'donors' ? 'active' : ''}`}
                style={activeTab === 'donors' ? { background: '#1a1a2e' } : {}}
                onClick={() => setActiveTab('donors')}>
                <i className="bi bi-people me-2" />{t('admin.tabDonors')}
              </button>
            </li>
          </ul>

          {activeTab === 'transactions' && (
            <>
              <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                <div className="row g-2 align-items-center">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-white"><i className="bi bi-search text-muted" /></span>
                      <input type="text" className="form-control border-start-0"
                        placeholder={t('admin.searchPlaceholder')}
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }} />
                      {search && (
                        <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); setPage(1); }}>
                          <i className="bi bi-x" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex gap-2 flex-wrap">
                      {['', 'completed', 'pending'].map(s => (
                        <button key={s}
                          className={`btn rounded-pill px-3 py-1 ${status === s ? 'btn-dark' : 'btn-outline-secondary'}`}
                          style={{ fontSize: '.85rem' }}
                          onClick={() => { setStatus(s); setPage(1); }}>
                          {s === '' ? t('admin.filterAll') : t(`admin.filter${s.charAt(0).toUpperCase() + s.slice(1)}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {apiError && (
                <div className="alert alert-danger rounded-4 d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-exclamation-triangle-fill fs-5" />
                  <div><strong>Error:</strong> {apiError}</div>
                </div>
              )}

              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 admin-table">
                    <thead>
                      <tr>
                        <th className="ps-4" style={{ width: 50 }}>#</th>
                        <th>{t('admin.colDate')}</th>
                        <th>領収書番号</th>
                        <th>{t('admin.colName')}</th>
                        <th>{t('admin.colAmount')}</th>
                        <th style={{ color: '#198754' }}>{t('admin.colNet')} (96.04%)</th>
                        <th style={{ color: '#fd7e14' }}>{t('admin.colFee')} (3.96%)</th>
                        <th>{t('admin.colFrequency')}</th>
                        <th>{t('admin.colStatus')}</th>
                        <th>{t('admin.colReceipt')}</th>
                        <th>{t('admin.colProfile')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={11} className="text-center py-5"><div className="spinner-border text-primary" /></td></tr>
                      ) : donations.length === 0 ? (
                        <tr><td colSpan={11} className="text-center py-5 text-muted">
                          <i className="bi bi-inbox fs-2 d-block mb-2" />{t('admin.noData')}
                        </td></tr>
                      ) : donations.map((d, idx) => {
                        const net = Math.floor(d.amount * NET_RATE);
                        const fee = d.amount - net;
                        return (
                          <tr key={d.id}>
                            <td className="ps-4 text-muted small">{(page - 1) * 20 + idx + 1}</td>
                            <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {new Date(d.created_at).toLocaleDateString('ja-JP')}<br />
                              <span style={{ fontSize: '.72rem' }}>
                                {new Date(d.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td>
                              {d.receipt_number
                                ? <span className="badge rounded-pill bg-light text-dark border" style={{ fontSize: '.72rem' }}>{d.receipt_number}</span>
                                : <span className="text-muted small">—</span>
                              }
                            </td>
                            <td className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>{d.name}</td>
                            <td className="fw-bold" style={{ color: 'var(--primary)' }}>{formatYen(d.amount)}</td>
                            <td className="fw-semibold" style={{ color: '#198754' }}>{formatYen(net)}</td>
                            <td className="small" style={{ color: '#fd7e14' }}>{formatYen(fee)}</td>
                            <td>
                              <span className="badge rounded-pill bg-light text-dark border">
                                {t(`admin.freq_${d.frequency}`)}
                              </span>
                            </td>
                            <td><StatusBadge status={d.status} t={t} /></td>
                            <td>
                              {d.receipt_number ? (
                                <button className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                  onClick={() => openReceipt(d.id)}>
                                  <i className="bi bi-receipt me-1" />{t('admin.viewReceipt')}
                                </button>
                              ) : <span className="text-muted small">—</span>}
                            </td>
                            <td>
                              {d.magic_token ? (
                                <a href={`/my-donations/${d.magic_token}`} target="_blank" rel="noreferrer"
                                  className="btn btn-sm btn-outline-secondary rounded-pill px-3">
                                  <i className="bi bi-person-lines-fill me-1" />{t('admin.viewProfile')}
                                </a>
                              ) : <span className="text-muted small">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination page={page} pages={pages} total={total} limit={20} onPage={setPage} t={t} />
              </div>
            </>
          )}

          {activeTab === 'donors' && (
            <>
              <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                <div className="input-group" style={{ maxWidth: 400 }}>
                  <span className="input-group-text bg-white"><i className="bi bi-search text-muted" /></span>
                  <input type="text" className="form-control border-start-0"
                    placeholder={t('admin.donorSearchPlaceholder')}
                    value={donorSearch}
                    onChange={e => { setDonorSearch(e.target.value); setDonorPage(1); }} />
                  {donorSearch && (
                    <button className="btn btn-outline-secondary" onClick={() => { setDonorSearch(''); setDonorPage(1); }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
              </div>

              {donorError && (
                <div className="alert alert-danger rounded-4 mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2" />{donorError}
                </div>
              )}

              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 admin-table">
                    <thead>
                      <tr>
                        <th className="ps-4" style={{ width: 50 }}>#</th>
                        <th>{t('admin.colName')}</th>
                        <th>{t('admin.colEmail')}</th>
                        <th>{t('admin.colDonationCount')}</th>
                        <th>{t('admin.colTotalDonated')}</th>
                        <th>{t('admin.colNet')} (96.04%)</th>
                        <th>{t('admin.colJoined')}</th>
                        <th>{t('admin.colProfile')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donorLoading ? (
                        <tr><td colSpan={8} className="text-center py-5"><div className="spinner-border text-primary" /></td></tr>
                      ) : donors.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-5 text-muted">
                          <i className="bi bi-inbox fs-2 d-block mb-2" />{t('admin.noData')}
                        </td></tr>
                      ) : donors.map((d, idx) => {
                        const net = Math.floor(d.total_donated * NET_RATE);
                        return (
                          <tr key={d.id}>
                            <td className="ps-4 text-muted small">{(donorPage - 1) * 20 + idx + 1}</td>
                            <td className="fw-semibold">{d.name}</td>
                            <td className="small text-muted">{d.email}</td>
                            <td className="text-center">
                              <span className="badge rounded-pill" style={{ background: '#e0e7ff', color: '#4361ee' }}>
                                {d.donation_count}
                              </span>
                            </td>
                            <td className="fw-bold" style={{ color: 'var(--primary)' }}>{formatYen(d.total_donated)}</td>
                            <td className="fw-semibold" style={{ color: '#198754' }}>{formatYen(net)}</td>
                            <td className="small text-muted">
                              {new Date(d.created_at).toLocaleDateString('ja-JP')}
                            </td>
                            <td>
                              {d.magic_token ? (
                                <a href={`/my-donations/${d.magic_token}`} target="_blank" rel="noreferrer"
                                  className="btn btn-sm btn-outline-secondary rounded-pill px-3">
                                  <i className="bi bi-person-lines-fill me-1" />{t('admin.viewProfile')}
                                </a>
                              ) : <span className="text-muted small">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination page={donorPage} pages={donorPages} total={donorTotal} limit={20} onPage={setDonorPage} t={t} />
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
